// Load-phase profiler for the planning map. The goal is to turn "the map feels slow" into concrete
// numbers: which phase (module download, map construction, style/glyph/sprite load, first tile render,
// data load) actually costs the wall-clock time. Logs to the browser console with a [map-perf] prefix so
// the phases are greppable. On in dev builds, off in production; flip either way with
// `localStorage.setItem('fp:perf','1'|'0')` in DevTools. See perfEnabled() for why.
//
// Usage: `const t = plog('phase A start')` then later `plog('phase B done', t)` logs the delta since t.
// Every call returns performance.now() so timings chain without threading a clock through call sites.

// The default follows the BUILD, not the storage key. This module is shared with havasaha, a public
// site, where "on unless the visitor sets fp:perf=0" meant every visitor's console got ~18 phase
// lines and two console.table dumps of the resource waterfall — developer instrumentation shipped to
// people who will never open DevTools, let alone set a key to turn it off.
//
// The key still overrides in BOTH directions, which is the part that matters for the dev tool: '1'
// forces the profiler on in a production build, so `vite build && vite preview` — the only way to
// measure realistic load — is still profilable, and '0' silences it during dev.
export function perfEnabled(): boolean {
  let key: string | null = null;
  try {
    key = localStorage.getItem('fp:perf');
  } catch {
    /* private mode / no storage → fall through to the build default */
  }
  if (key === '1') return true;
  if (key === '0') return false;
  return import.meta.env.DEV;
}

export function plog(label: string, sinceMs?: number): number {
  const now = performance.now();
  if (perfEnabled()) {
    if (sinceMs != null) console.info(`[map-perf] ${label}: +${Math.round(now - sinceMs)}ms`);
    else console.info(`[map-perf] ${label}`);
  }
  return now;
}

// Time a synchronous block and warn only if it's slow enough to drop a frame — for the runtime hot paths
// (deck.gl render, terrain re-anchor, hover) that can freeze the UI during interaction. Returns fn()'s value.
export function ptime<T>(label: string, fn: () => T, thresholdMs = 30): T {
  if (!perfEnabled()) return fn();
  const t = performance.now();
  const r = fn();
  const d = performance.now() - t;
  if (d >= thresholdMs) console.warn(`[map-perf] SLOW ${label}: ${Math.round(d)}ms`);
  return r;
}

// The single most useful "why did it freeze" tool: the browser reports every task that blocks the main
// thread ≥50 ms (the responsiveness threshold). This catches interaction freezes (3D pan/rotate) that the
// load-phase timers can't see, with the duration of each stall. ('longtask' is unsupported on Safari/
// WKWebView — the tauri build — so it's a no-op there; the per-path ptime() calls still cover that case.)
let longTaskObs: PerformanceObserver | null = null;
export function startLongTaskMonitor(): void {
  if (longTaskObs || !perfEnabled() || typeof PerformanceObserver === 'undefined') return;
  try {
    longTaskObs = new PerformanceObserver((list) => {
      if (!perfEnabled()) return;
      for (const e of list.getEntries()) {
        console.warn(`[map-perf] ⚠ LONG TASK ${Math.round(e.duration)}ms — main thread blocked @${Math.round(e.startTime)}ms`);
      }
    });
    longTaskObs.observe({ entryTypes: ['longtask'] });
  } catch { /* entryType unsupported → skip */ }
}
export function stopLongTaskMonitor(): void {
  longTaskObs?.disconnect();
  longTaskObs = null;
}

// The phase timers above tell us WHEN the map became usable but not WHY — which fetch cost the time. This
// reads the browser's Resource Timing API for the actual network waterfall: exact start/duration/size per
// URL, so "the sprite was ~2 s" stops being an inference and becomes a measured row. Filtered to the map's
// hosts and grouped by resource kind (sprite / glyphs / tiles / TileJSON) so the dominant cost is obvious.
const MAP_HOST = /openfreemap|arcgis|cartocdn|amazonaws|basemaps\.carto/;
// Classify by HOST + path, not file extension — the arcgis satellite tiles have NO extension (…/tile/z/y/x),
// which is why the extension-based version dumped everything as "other". Each host maps to one resource role.
function kindOf(url: string): string {
  if (/arcgisonline\.com/.test(url)) return 'sat-imagery';
  if (/static-map-tiles-api/.test(url)) return /\/imagery\//.test(url) ? 'sat-imagery' : 'streets-raster';
  // Only the no-WebGL fallback can produce these now; the WebGL engine draws streets from vectors.
  if (/cartocdn|basemaps\.carto/.test(url)) return 'streets-raster';
  if (/elevation-tiles|amazonaws/.test(url)) return 'dem-terrain'; // 3D relief / hillshade / contours
  if (/openfreemap\.org\/fonts/.test(url)) return 'glyphs';
  if (/openfreemap\.org\/sprites/.test(url)) return 'sprite';
  if (/openfreemap\.org\/planet\//.test(url)) return 'vector-tile'; // /planet/{z}/{x}/{y}.pbf (labels/POI/buildings)
  if (/openfreemap\.org\/planet/.test(url)) return 'vector-tilejson'; // /planet (the metadata json)
  return 'other';
}
export function dumpResourceTimings(label: string): void {
  if (!perfEnabled()) return;
  try {
    const es = (performance.getEntriesByType('resource') as PerformanceResourceTiming[]).filter((e) => MAP_HOST.test(e.name));
    // Cross-origin tiles (arcgis/openfreemap) don't send Timing-Allow-Origin, so transferSize is 0 — sizes
    // are unavailable, but startTime/duration/responseEnd ARE. So we report the useful, real metrics:
    //   n        = how many of this kind were fetched
    //   slowest  = worst single fetch (ms)
    //   done@    = responseEnd of the LAST one (ms since page load — comparable to the phase timestamps)
    // Summing durations was the bug: parallel fetches overlap, so the sum (8762ms) is meaningless wall-clock.
    const byKind: Record<string, { n: number; slowest: number; lastEnd: number }> = {};
    for (const e of es) {
      const b = (byKind[kindOf(e.name)] ??= { n: 0, slowest: 0, lastEnd: 0 });
      b.n++;
      b.slowest = Math.max(b.slowest, e.duration);
      b.lastEnd = Math.max(b.lastEnd, e.responseEnd);
    }
    const summary = Object.entries(byKind)
      .sort((a, b) => b[1].lastEnd - a[1].lastEnd) // what finished LAST is the tail that gates "settled"
      .map(([k, v]) => `${k} ${v.n}× (slowest ${Math.round(v.slowest)}ms, done@${Math.round(v.lastEnd)}ms)`)
      .join('  ·  ');
    console.info(`[map-perf] ${label}: ${summary || '(no map fetches yet)'}`);
    const rows = es
      .map((e) => ({ kind: kindOf(e.name), start: Math.round(e.startTime), dur: Math.round(e.duration), end: Math.round(e.responseEnd), url: e.name.replace(/^https?:\/\//, '').slice(0, 70) }))
      .sort((a, b) => b.dur - a.dur);
    console.groupCollapsed(`[map-perf] ${label} — ${rows.length} map fetches (expand for per-URL)`);
    console.table(byKind);
    console.table(rows);
    console.groupEnd();
  } catch { /* Resource Timing unsupported → skip */ }
}
